import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, LayoutList, LayoutGrid } from 'lucide-react';
import { cva } from 'class-variance-authority';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { createPlaceholderUrl } from '../../../../utils/PlaceholderCreator';

// ─── filtrarSkills — función privada (P6-A + P6-C base) ──────────────────────
// Toda la lógica de filtrado vive aquí, fuera del componente.
// Recibe el filtro de color como parámetro para P6-A.
function filtrarSkills(allSkills, searchTerm, colorFilter = 'Todos', viewMode = 'list') {
    if (!searchTerm || searchTerm.length < 2) return [];

    const lowerTerm = searchTerm.toLowerCase();
    const normalizedTerm = lowerTerm.replace(/[^a-z0-9]/g, '');

    const results = allSkills.filter(skill => {
        // Excluir Unique Skills siempre
        if (skill.color === 'Unique Skills') return false;

        // Filtro de color (P6-A)
        if (colorFilter !== 'Todos' && skill.color !== colorFilter) return false;

        const name     = skill.Skill_name    ? skill.Skill_name.toLowerCase()    : '';
        const nameJP   = skill.Skill_name_jp ? skill.Skill_name_jp.toLowerCase() : '';

        // Match 1: estándar (preserva espacios/símbolos)
        if (name.includes(lowerTerm) || nameJP.includes(lowerTerm)) return true;

        // Match 2: normalizado (ignora símbolos como ' ! ?)
        if (normalizedTerm.length > 0) {
            const normName   = name.replace(/[^a-z0-9]/g, '');
            const normNameJP = nameJP.replace(/[^a-z0-9]/g, '');
            if (normName.includes(normalizedTerm) || normNameJP.includes(normalizedTerm)) return true;
        }

        return false;
    });

    // P10: en modo grid se muestran todos los resultados; en lista máximo 10
    return viewMode === 'grid' ? results : results.slice(0, 10);
}

// ─── Configuración de chips de color (P6-A) ────────────────────────────────
const COLOR_FILTERS = [
    { label: 'Todos',           color: 'Todos',            dot: null },
    { label: 'Green',           color: 'Green Skills',     dot: '#4ade80' },
    { label: 'Red',             color: 'Red Skills',       dot: '#f87171' },
    { label: 'Blue',            color: 'Blue Skills',      dot: '#60a5fa' },
    { label: 'Orange',          color: 'Orange Skills',    dot: '#fb923c' },
    { label: 'Gold',            color: 'Gold Skills',      dot: '#fbbf24' },
    { label: 'Inherited',       color: 'Inherited Skills', dot: '#a78bfa' },
];

// CVA: variante del chip de color
const chipVariants = cva(
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-150 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
    {
        variants: {
            active: {
                true:  'bg-gray-800 text-white border-gray-600 shadow-sm',
                false: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600',
            },
        },
        defaultVariants: { active: false },
    }
);

// Variantes de animación del dropdown
const dropdownVariants = {
    hidden: { opacity: 0, y: -6, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.14, ease: 'easeOut' } },
    exit:   { opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.10, ease: 'easeIn' } },
};

const itemVariants = {
    hidden:  { opacity: 0, x: -6 },
    visible: (i) => ({
        opacity: 1, x: 0,
        transition: { duration: 0.12, ease: 'easeOut', delay: i * 0.03 },
    }),
};

// ─── SkillSearch ───────────────────────────────────────────────────────────────
const SkillSearch = ({ allSkills, onAddSkill, activeSkills }) => {
    const { t } = useTranslation();
    const { isThemeChanging } = useTheme();

    const [searchTerm, setSearchTerm]         = useState('');
    const [isOpen, setIsOpen]                 = useState(false);
    const [filteredSkills, setFilteredSkills] = useState([]);
    const [lastResults, setLastResults]       = useState([]); // P9
    const [pendingSkill, setPendingSkill]     = useState(null);
    const [pendingLevel, setPendingLevel]     = useState(0);
    const [colorFilter, setColorFilter]       = useState('Todos'); // P6-A
    const [viewMode, setViewMode]             = useState('list'); // P10: 'list' | 'grid'

    const wrapperRef         = useRef(null);
    const inputRef            = useRef(null);
    const lastSearchTermRef   = useRef(''); // P9+P6-A: último término para re-filtrar con colorFilter

    // ─── Lógica de filtrado (usa filtrarSkills privada) ─────────────────────
    useEffect(() => {
        // El término efectivo: el actual si es válido, o el último guardado (P9)
        const effectiveTerm = searchTerm.length > 1 ? searchTerm : lastSearchTermRef.current;

        if (effectiveTerm.length > 1) {
            const results = filtrarSkills(allSkills, effectiveTerm, colorFilter, viewMode);
            setFilteredSkills(results);
            if (searchTerm.length > 1) {
                setLastResults(results);
                lastSearchTermRef.current = searchTerm; // guardar término activo
            }
            setIsOpen(true);
        } else {
            setFilteredSkills([]);
            setIsOpen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, allSkills, activeSkills, colorFilter, viewMode]);

    // ─── Click outside ────────────────────────────────────────────────────────
    useEffect(() => {
        function handleClickOutside(event) {
            // Ignorar clicks dentro del Dialog portal (botones de nivel, overlay, etc.)
            // El dialog tiene role="dialog"; sus hijos viven fuera del wrapperRef
            if (event.target.closest('[role="dialog"]')) return;
            // Ignorar también el overlay del dialog
            if (event.target.dataset.radixDialogOverlay !== undefined) return;

            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setPendingSkill(null);
                setLastResults([]);
                lastSearchTermRef.current = '';
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleSelect = (skill, level = 0) => {
        const isGold = skill.color && skill.color.toLowerCase().includes('gold');
        if (isGold) {
            pendingSkillRef.current = skill;
            pendingLevelRef.current = level;
            setPendingSkill(skill);
            setPendingLevel(level);
            return;
        }

        // Verificar si ya está en el pool (actualización de nivel vs. nueva)
        const alreadyActive = activeSkills.find(s => s.skill.Id === skill.Id);
        onAddSkill(skill, level, level);

        // Crítico 2: toast.success al agregar / actualizar nivel
        if (alreadyActive) {
            toast(t('SkillSearch.toast_level_updated', { level: level === 5 ? 'Max' : level }), {
                description: skill.Skill_name,
                duration: 1800,
            });
        } else {
            toast.success(t('SkillSearch.toast_skill_added'), {
                description: skill.Skill_name,
                duration: 1800,
            });
        }

        setSearchTerm('');
        if (inputRef.current) inputRef.current.focus();
    };

    // useCallback para que el handler siempre lea la ref más reciente del state
    const pendingSkillRef = useRef(null);
    const pendingLevelRef = useRef(0);

    const handleBaseLevelSelect = useCallback((baseLevel) => {
        const skill = pendingSkillRef.current;
        const level = pendingLevelRef.current;
        if (!skill) return;

        onAddSkill(skill, level, baseLevel);
        toast.success(t('SkillSearch.toast_skill_added'), {
            description: skill.Skill_name,
            duration: 1800,
        });
        setPendingSkill(null);
        setPendingLevel(0);
        pendingSkillRef.current = null;
        pendingLevelRef.current = 0;
        setSearchTerm('');
        setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 50);
    }, [onAddSkill, t]);

    const getIconUrl = (iconId) => {
        if (!iconId) return null;
        return `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;
    };

    const clearSearch = () => {
        setSearchTerm('');
        setLastResults([]);
        lastSearchTermRef.current = '';
        setIsOpen(false);
        if (inputRef.current) inputRef.current.focus();
    };

    // ─── JSX ──────────────────────────────────────────────────────────────────
    return (
        <div
            className="relative mb-4"
            ref={wrapperRef}
            style={{ '--stagger-delay': isThemeChanging ? 'inherit' : '0ms' }}
        >
            {/* Label */}
            <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                {t('SkillSearch.label_add_skill')}
            </label>

            {/* Fila chips + toggle — misma línea, toggle a la derecha (P6-A + P10) */}
            <div className="flex items-start justify-between gap-3 mb-2.5">
                <div className="flex flex-wrap gap-1.5">
                    {COLOR_FILTERS.map(filter => (
                        <motion.button
                            key={filter.label}
                            onClick={() => setColorFilter(filter.color)}
                            className={chipVariants({ active: colorFilter === filter.color })}
                            whileTap={{ scale: 0.92 }}
                            aria-pressed={colorFilter === filter.color}
                        >
                            {filter.dot && (
                                <span
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: filter.dot }}
                                />
                            )}
                            {filter.label}
                        </motion.button>
                    ))}
                </div>

                {/* Toggle Lista / Grid (P10) — derecha, sin fila extra */}
                <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 flex-shrink-0 mt-0.5">
                    <motion.button
                        onClick={() => setViewMode('list')}
                        whileTap={{ scale: 0.9 }}
                        title="Vista lista"
                        className={`p-1.5 rounded-md transition-all duration-150 ${
                            viewMode === 'list'
                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                        aria-label="Vista lista"
                        aria-pressed={viewMode === 'list'}
                    >
                        <LayoutList size={15} />
                    </motion.button>
                    <motion.button
                        onClick={() => setViewMode('grid')}
                        whileTap={{ scale: 0.9 }}
                        title="Vista grid"
                        className={`p-1.5 rounded-md transition-all duration-150 ${
                            viewMode === 'grid'
                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                        aria-label="Vista grid"
                        aria-pressed={viewMode === 'grid'}
                    >
                        <LayoutGrid size={15} />
                    </motion.button>
                </div>
            </div>

            {/* Input con ícono lucide-react */}
            <div className="relative">
                <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
                />
                <input
                    ref={inputRef}
                    type="text"
                    className="
                        form-input w-full pl-9 pr-9 py-2.5
                        border border-gray-200 dark:border-gray-600
                        rounded-xl bg-white dark:bg-gray-700
                        text-gray-800 dark:text-gray-100
                        placeholder:text-gray-400 dark:placeholder:text-gray-500
                        focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                        outline-none transition-all duration-200
                        text-sm
                    "
                    placeholder={t('SkillSearch.placeholder_search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => (searchTerm.length > 1 || lastResults.length > 0) && setIsOpen(true)}
                />
                {/* Botón clear */}
                <AnimatePresence>
                    {searchTerm.length > 0 && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.7 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.7 }}
                            transition={{ duration: 0.1 }}
                            onClick={clearSearch}
                            className="absolute right-2.5 inset-y-0 my-auto flex items-center justify-center w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full transition-colors"
                            aria-label="Limpiar búsqueda"
                        >
                            <X size={15} />
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>

            {/* Gold Skill Dialog — usa token uma-pink */}
            <Dialog.Root
                open={!!pendingSkill}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingSkill(null);
                        setPendingLevel(0);
                        pendingSkillRef.current = null;
                        pendingLevelRef.current = 0;
                    }
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
                    <Dialog.Content
                        className="
                            fixed left-1/2 top-1/2 z-50
                            -translate-x-1/2 -translate-y-1/2
                            w-full max-w-sm
                            bg-white dark:bg-gray-800
                            rounded-2xl shadow-2xl shadow-black/30
                            border-2 border-uma-pink
                            p-6
                            focus:outline-none
                        "
                        onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                        <Dialog.Title className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-1 text-center">
                            {t('SkillSearch.modal_title_base_level')}
                        </Dialog.Title>
                        {pendingSkill && (
                            <>
                                <Dialog.Description className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
                                    {t('SkillSearch.modal_subtitle_base_level', {
                                        skillName: pendingSkill.Skill_name,
                                        level: pendingLevel === 5 ? 'M' : pendingLevel,
                                    })}
                                </Dialog.Description>
                                <div className="flex justify-center gap-2">
                                    {[0, 1, 2, 3, 4, 5].map((level) => (
                                        <motion.button
                                            key={level}
                                            onClick={() => handleBaseLevelSelect(level)}
                                            whileHover={{ scale: 1.12 }}
                                            whileTap={{ scale: 0.92 }}
                                            className={`
                                                w-10 h-10 flex items-center justify-center rounded-full
                                                text-sm font-bold border transition-all
                                                ${level === 5
                                                    ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600 hover:bg-violet-200'
                                                    : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-blue-100 hover:text-blue-600 hover:border-blue-300 dark:hover:bg-blue-900/30'
                                                }
                                            `}
                                        >
                                            {level === 5 ? 'M' : level}
                                        </motion.button>
                                    ))}
                                </div>
                            </>
                        )}
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            {/* Dropdown de resultados */}
            <AnimatePresence>
                {isOpen && !pendingSkill && filteredSkills.length > 0 && (
                    <motion.div
                        variants={dropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="
                            absolute z-10 w-full mt-1.5
                            bg-white dark:bg-gray-800
                            rounded-xl shadow-2xl shadow-black/10 dark:shadow-black/40
                            border border-gray-100 dark:border-gray-700
                            max-h-80 overflow-y-auto
                            scrollbar-thin scrollbar-track-gray-100 dark:scrollbar-track-gray-800
                            scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600
                            scrollbar-thumb-rounded-full
                        "
                    >
                        {/* Indicador de lastResults (P9) */}
                        {searchTerm.length === 0 && lastResults.length > 0 && (
                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                                Última búsqueda
                            </div>
                        )}

                        {/* Vista LISTA */}
                        {viewMode === 'list' && filteredSkills.map((skill, i) => {
                            const activeState  = activeSkills.find(s => s.skill.Id === skill.Id);
                            const isActive     = !!activeState;
                            const currentLevel = activeState ? activeState.level : null;

                            let baseLevelInfo = null;
                            if (isActive && skill.base_skill_id) {
                                const baseItem = activeSkills.find(s =>
                                    parseInt(s.skill.Id_skill, 10) === parseInt(skill.base_skill_id, 10)
                                );
                                if (baseItem) baseLevelInfo = baseItem.level;
                            }

                            return (
                                <motion.div
                                    key={skill.Id}
                                    custom={i}
                                    initial="hidden"
                                    animate="visible"
                                    variants={itemVariants}
                                    className={`
                                        flex items-center gap-3 px-3 py-2.5
                                        border-b border-gray-50 dark:border-gray-700/50 last:border-0
                                        transition-colors duration-150 group
                                        ${isActive
                                            ? 'bg-emerald-50/60 hover:bg-emerald-100/60 dark:bg-emerald-900/15 dark:hover:bg-emerald-900/25'
                                            : 'hover:bg-blue-50/80 dark:hover:bg-gray-700/60'
                                        }
                                    `}
                                >
                                    <div
                                        onClick={() => handleSelect(skill, 0)}
                                        className="flex-1 flex items-center gap-2.5 cursor-pointer min-w-0"
                                    >
                                        <div className="relative w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                                            <img
                                                src={getIconUrl(skill.icon_id)}
                                                alt=""
                                                className={`w-full h-full object-cover ${isActive ? 'opacity-85' : ''}`}
                                                onError={(e) => { e.target.src = createPlaceholderUrl(36, 36, '?'); }}
                                            />
                                            {isActive && (
                                                <div className="absolute inset-0 bg-emerald-500/25 flex items-center justify-center">
                                                    <svg className="w-5 h-5 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm flex items-center gap-1.5 flex-wrap">
                                                <span className="truncate">{skill.Skill_name}</span>
                                                {isActive && (
                                                    <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-700 font-bold flex-shrink-0">
                                                        Lv {currentLevel}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-gray-500 dark:text-gray-400 flex gap-2 flex-wrap">
                                                <span className="truncate">{skill.Skill_name_jp}</span>
                                                {baseLevelInfo !== null && (
                                                    <span className="text-orange-500 dark:text-orange-400 font-semibold flex-shrink-0">
                                                        • Base Lv {baseLevelInfo}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center bg-gray-100 dark:bg-gray-700/80 rounded-lg p-0.5 gap-0.5 flex-shrink-0">
                                        {[0, 1, 2, 3, 4, 5].map((level) => {
                                            const isCurrentLevel = isActive && currentLevel === level;
                                            return (
                                                <button
                                                    key={level}
                                                    onClick={(e) => { e.stopPropagation(); handleSelect(skill, level); }}
                                                    className={`
                                                        w-6 h-6 flex items-center justify-center text-xs font-bold rounded-md transition-all duration-100
                                                        ${isCurrentLevel
                                                            ? (level === 5 ? 'bg-violet-600 text-white shadow-sm' : 'bg-blue-600 text-white shadow-sm')
                                                            : (level === 5 ? 'text-violet-500 dark:text-violet-400 hover:bg-white dark:hover:bg-gray-600' : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600')
                                                        }
                                                    `}
                                                    aria-label={level === 5 ? 'Max' : `Nivel ${level}`}
                                                >
                                                    {level === 5 ? 'M' : level}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            );
                        })}

                        {/* Vista GRID (P10) — auto-fill responsive, sin columnas fijas */}
                        {viewMode === 'grid' && (
                            <div
                                className="p-3 overflow-y-auto"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                    gap: '10px',
                                    maxHeight: '420px',
                                }}
                            >
                                {filteredSkills.map((skill, i) => {
                                    const activeState  = activeSkills.find(s => s.skill.Id === skill.Id);
                                    const isActive     = !!activeState;
                                    const currentLevel = activeState ? activeState.level : null;
                                    return (
                                        <motion.div
                                            key={skill.Id}
                                            custom={i}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.12, delay: Math.min(i * 0.02, 0.3) }}
                                            className={`
                                                relative flex flex-col items-center gap-2 p-2.5 rounded-xl border
                                                transition-all duration-150 cursor-pointer
                                                ${isActive
                                                    ? 'bg-emerald-50 dark:bg-emerald-900/25 border-emerald-400 dark:border-emerald-600 shadow-sm'
                                                    : 'bg-gray-50 dark:bg-gray-750 border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50/60 dark:hover:bg-gray-700'
                                                }
                                            `}
                                        >
                                            {/* Ícono grande */}
                                            <div
                                                className="relative w-13 h-13 rounded-xl overflow-hidden flex-shrink-0 shadow-sm"
                                                style={{ width: '52px', height: '52px' }}
                                                onClick={() => handleSelect(skill, 0)}
                                            >
                                                <img
                                                    src={getIconUrl(skill.icon_id)}
                                                    alt={skill.Skill_name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { e.target.src = createPlaceholderUrl(52, 52, '?'); }}
                                                />
                                                {isActive && (
                                                    <div className="absolute inset-0 bg-emerald-500/30 flex items-center justify-center">
                                                        <svg className="w-5 h-5 text-white drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Nombre */}
                                            <div
                                                onClick={() => handleSelect(skill, 0)}
                                                className="w-full text-center"
                                            >
                                                {isActive && (
                                                    <div className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 mb-0.5 uppercase tracking-wide">
                                                        Lv {currentLevel === 5 ? 'M' : currentLevel}
                                                    </div>
                                                )}
                                                <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-200 leading-tight line-clamp-2 text-center">
                                                    {skill.Skill_name}
                                                </div>
                                            </div>

                                            {/* Botones de nivel — pill style */}
                                            <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 w-full justify-center">
                                                {[0,1,2,3,4,5].map(level => {
                                                    const isCurrent = isActive && currentLevel === level;
                                                    return (
                                                        <button
                                                            key={level}
                                                            onClick={(e) => { e.stopPropagation(); handleSelect(skill, level); }}
                                                            className={`
                                                                flex-1 h-6 flex items-center justify-center text-[11px] font-bold rounded-md transition-all duration-100
                                                                ${isCurrent
                                                                    ? (level === 5 ? 'bg-violet-600 text-white shadow-sm' : 'bg-blue-600 text-white shadow-sm')
                                                                    : (level === 5
                                                                        ? 'text-violet-500 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-900/40'
                                                                        : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600'
                                                                    )
                                                                }
                                                            `}
                                                            aria-label={level === 5 ? 'Max' : `Nivel ${level}`}
                                                        >
                                                            {level === 5 ? 'M' : level}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Sin resultados */}
            <AnimatePresence>
                {isOpen && searchTerm.length > 1 && filteredSkills.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.12 }}
                        className="absolute z-10 w-full mt-1.5 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400 text-sm"
                    >
                        {t('SkillSearch.msg_no_skills')}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SkillSearch;
// Revisado: 2026-03-27
